import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { CircleOff } from "lucide-react-native";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useSexualHealth } from "../SexualHealthContext";
import type { STDDetail } from "../SexualHealthContext";
import { buildSexualHealthNavigationStack } from "../buildSexualHealthNavigationStack";
import { STD_OPTIONS } from "../options";
import type { STDType } from "../options";
import type { SexualHealthStackParamList } from "../SexualHealthNavigator";

export function STDsScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useSexualHealth();
  const navigation =
    useNavigation<NavigationProp<SexualHealthStackParamList>>();

  const selectedSTDs = useMemo(
    () => new Set(state.stds.map((s) => s.stdType)),
    [state.stds],
  );

  const isNone = state.hadSTDs === false;

  const toggleSTD = (stdType: STDType) => {
    if (selectedSTDs.has(stdType)) {
      // Remove
      const toRemove = state.stds.find((s) => s.stdType === stdType);
      const idsToDelete = toRemove?.id
        ? [...state.stdIdsToDelete, toRemove.id]
        : state.stdIdsToDelete;
      const remaining = state.stds.filter((s) => s.stdType !== stdType);
      updateState({
        hadSTDs: remaining.length > 0 ? true : null,
        stds: remaining,
        stdIdsToDelete: idsToDelete,
      });
    } else {
      // Add
      const newSTD: STDDetail = { stdType };
      updateState({
        hadSTDs: true,
        stds: [...state.stds, newSTD],
      });
    }
  };

  const handleNone = () => {
    const idsToDelete = state.stds.filter((s) => s.id).map((s) => s.id!);
    updateState({
      hadSTDs: false,
      stds: [],
      stdIdsToDelete: [...state.stdIdsToDelete, ...idsToDelete],
    });
  };

  const handleSave = async () => {
    await save();
    const stack = buildSexualHealthNavigationStack(state);
    const currentIdx = stack.indexOf("STDs");
    const nextRoute = stack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  const isValid = isNone || (state.hadSTDs === true && state.stds.length > 0);

  return (
    <QuestionLayout
      title={t("med_history.gynae.sexual_health.stds_title")}
      subtitle={t("med_history.gynae.sexual_health.stds_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={!isValid}
      saveLabel={t("med_history.gynae.sexual_health.continue")}
    >
      <View style={styles.container}>
        <View style={styles.grid}>
          {STD_OPTIONS.map((option) => (
            <View key={option.id} style={styles.cell}>
              <SquareOptionButton
                icon={option.icon}
                label={t(option.labelKey)}
                isSelected={!isNone && selectedSTDs.has(option.id)}
                onPress={() => {
                  if (isNone) {
                    updateState({
                      hadSTDs: true,
                      stds: [{ stdType: option.id }],
                    });
                  } else {
                    toggleSTD(option.id);
                  }
                }}
                labelSize="sm"
              />
            </View>
          ))}
          <View style={styles.cell}>
            <SquareOptionButton
              icon={CircleOff}
              label={t("med_history.gynae.sexual_health.none")}
              isSelected={isNone}
              onPress={handleNone}
              labelSize="sm"
            />
          </View>
        </View>
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    width: "100%",
  },
  cell: {
    width: "47%",
  },
});
